import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import { mergeConfig } from 'Components/Worksheets/Core/Helpers/configHelpers';
import getDerivedFromMeasurementsConfig from 'Components/Worksheets/MoistureContentWorksheet/derivedFromMeasurementsConfig';
import { FormatTypes } from 'Lib/Format';
import * as oPath from '../../../../../Lib/Object/Path/Immutable';
import qestIds from '../../../QestIds';
import getMeasuredUsingConfig from '../../measuredUsingConfig';
import { GrainSize } from '../../types';
import getLabelConfig from './labelConfig';

const getDefaultReadability = (configArgs: IConfigFunctionArgs, prefix: string) => {
  const grainSize = oPath.get(configArgs.worksheet, `${prefix}.Preparation.GrainSize`) as GrainSize;
  let defaultReadability;
  switch (grainSize) {
    case GrainSize.Coarse:
      // BS 1377-2 Cl. 3.2.3.3.1
      defaultReadability = 1;
      break;
    case GrainSize.Medium:
      // BS 1377-2 Cl. 3.2.3.2.1
      defaultReadability = 0.1;
      break;
    case GrainSize.Fine:
    default:
      // BS 1377-2 Cl. 3.2.3.1.1
      defaultReadability = 0.01;
      break;
  }

  return {
    defaultReadability,
    defaultUom: 'g',
  };
};

//BS 1377-2 has two statements that are relevant to the display of moisture content values
//
//3.2.4 Calculations and expression of results
//
// "Calculate the moisture content of the soil specimen, w, as a percentage of the dry soil mass to
// the nearest 0.1 %, from the equation" ...
//
//and a little further on, when talking about the report:
//
// "Express the moisture content of the soil for values up to 10 % to two significant figures.
// For moisture contents above 10 % express the result to the nearest whole number."
//
//Since this isn't a test report, it makes sense to round to the "nearest 0.1".
const getMoistureContentPercentFormat = { nearest: 0.1 };

const getConfig = (prefix: string) =>
  mergeConfig(
    {
      [`${prefix}.Equipment.DryingTemperature`]: { uom: '°C', format: { nearest: 1 } },
      [`${prefix}.Preparation.SaltContentGl`]: { uom: 'g/L', format: { nearest: 1 } },
      [`${prefix}.Preparation.FluidDensity`]: { uom: 'Mg/m³', format: { nearest: 0.001 } },
      [`${prefix}.Preparation.SaltContentPpt`]: { uom: 'ppt', format: { nearest: 1 } },
      [`${prefix}.DryingTray.DryMassReadings.DryingTime`]: { uom: 'h', format: { nearest: 0.01 } },
      [`${prefix}.DryingTray.DateTimePlacedInOvenLocal`]: {
        format: { type: FormatTypes.Time, pattern: 'YYYY-MM-DD HH:mm' },
      },
      [`${prefix}.DryingTray.DryMassReadings.DateTimeRemovedFromOvenLocal`]: {
        format: { type: FormatTypes.Time, pattern: 'YYYY-MM-DD HH:mm' },
      },

      [`${prefix}.Results.MoistureContentPercent`]: { format: getMoistureContentPercentFormat },
      [`${prefix}.Results.FluidContent`]: { format: getMoistureContentPercentFormat },
    },
    getMeasuredUsingConfig(prefix),
    getDerivedFromMeasurementsConfig(prefix),
    getLabelConfig(prefix),
    {
      [`${prefix}.Equipment.DryMassBalance`]: (configArgs: IConfigFunctionArgs) =>
        getDefaultReadability(configArgs, prefix),
      [`${prefix}.Equipment.Balance`]: (configArgs: IConfigFunctionArgs) => getDefaultReadability(configArgs, prefix),
    }
  );

export { getConfig, getMoistureContentPercentFormat };

export default {
  [qestIds.moistureContent.BS_1377_2.rev1990]: getConfig,
};
